import { z } from 'zod'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'
import { HexNumber } from '@lukso/controllerapps-sharedlogic-primitives'

import { AddressPermissionsLength } from './AddressPermissionsLength'

const AddressPermissionsIndexPropsSchema = z.object({
  addressPermissionsIndexValue: z.instanceof(HexNumber),
})

type AddressPermissionsIndexProps = z.infer<
  typeof AddressPermissionsIndexPropsSchema
>

export class AddressPermissionsIndex extends ValueObject<AddressPermissionsIndexProps> {
  public getValue(): HexNumber {
    return this.props.addressPermissionsIndexValue
  }

  /**
   * When used in the controller address data key, the index must be 0-padded to 32 characters. This is not prefixed
   * with 0x.
   * @returns The 0-padded value
   */
  public getPadded32Value(): string {
    const stringValue0x = this.props.addressPermissionsIndexValue.getValue()
    const stringValue = stringValue0x.slice(2)
    return stringValue.padStart(32, '0')
  }

  public equals(other: AddressPermissionsIndex): boolean {
    const value1 = this.getValue().getValue().toLowerCase()
    const value2 = other.getValue().getValue().toLowerCase()
    return value1 === value2
  }

  public static create(
    props: AddressPermissionsIndexProps
  ): AddressPermissionsIndex {
    const validatedProps = AddressPermissionsIndexPropsSchema.parse(props)
    return new AddressPermissionsIndex(validatedProps)
  }

  /**
   * Creates an array of AddressPermissionsIndex from the length of the controller AddressPermissions array.
   * @param length The length of the controller AddressPermissions array.
   * @returns An array containing AddressPermissionsIndex for each index in the array.
   */
  public static createIndicesFromControllerLength(
    length: AddressPermissionsLength
  ): AddressPermissionsIndex[] {
    const lengthValue = length.getDecimalValue()
    const indices: AddressPermissionsIndex[] = []
    for (let i = 0; i < lengthValue; i++) {
      const index = HexNumber.createFromDecimal(i)
      indices.push(
        AddressPermissionsIndex.create({ addressPermissionsIndexValue: index })
      )
    }
    return indices
  }
}
